import { useState, useEffect, useRef } from 'react';
import { Bell, X, Swords, Trophy, Calendar, BarChart3, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('typebots_token');
      if (!token) return;
      
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('typebots_token');
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('typebots_token');
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem('typebots_token');
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'duel_challenge':
        return <Swords className="w-5 h-5 text-glitch-red" />;
      case 'duel_result':
        return <Trophy className="w-5 h-5 text-zap-yellow" />;
      case 'weekly_summary':
        return <BarChart3 className="w-5 h-5 text-bot-cyan" />;
      case 'class_challenge':
        return <Trophy className="w-5 h-5 text-glitch-red" />;
      default:
        return <Bell className="w-5 h-5 text-bot-cyan" />;
    }
  };

  const getNotificationLink = (notification) => {
    if (notification.type === 'duel_challenge' && notification.data?.duel_id) {
      return `/duel/${notification.data.duel_id}`;
    }
    if (notification.type === 'duel_result' && notification.data?.duel_id) {
      return `/duel/${notification.data.duel_id}`;
    }
    return null;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-screen-white/70 hover:text-screen-white transition-colours"
        data-testid="notification-bell"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-glitch-red text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-dark-panel border-2 border-mid-panel rounded-xl shadow-xl z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between p-4 border-b border-mid-panel">
            <h3 className="font-display text-lg text-screen-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-bot-cyan text-sm hover:underline flex items-center gap-1"
              >
                <Check className="w-4 h-4" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-screen-white/30 mx-auto mb-3" />
                <p className="font-body text-sm text-screen-white/50">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const link = getNotificationLink(notification);
                const Content = (
                  <div
                    className={`p-4 border-b border-mid-panel/50 hover:bg-deep-space/50 transition-colours ${
                      !notification.read ? 'bg-bot-cyan/5' : ''
                    }`}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`font-body text-sm ${!notification.read ? 'text-screen-white font-semibold' : 'text-screen-white/80'}`}>
                            {notification.title}
                          </p>
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteNotification(notification.id); }}
                            className="text-screen-white/30 hover:text-glitch-red flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="font-body text-xs text-screen-white/60 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="font-mono text-xs text-screen-white/40 mt-2">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );

                if (link) {
                  return (
                    <Link
                      key={notification.id}
                      to={link}
                      onClick={() => { markAsRead(notification.id); setIsOpen(false); }}
                    >
                      {Content}
                    </Link>
                  );
                }

                return (
                  <div
                    key={notification.id}
                    onClick={() => markAsRead(notification.id)}
                    className="cursor-pointer"
                  >
                    {Content}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
